import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../ui/table';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { StatusBadge } from '../ui/status-badge';
import { StatsCard } from '../ui/stats-card';
import { Skeleton } from '../ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../ui/alert-dialog';
import {
  Search,
  Plus,
  Send,
  Eye,
  Trash2,
  Loader2,
  Bell,
  Users,
  TrendingUp,
  Edit
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  NotificationAPI,
  Notification,
  NotificationTemplate,
  NotificationDashboard,
  NotificationCreateRequest
} from '../../lib/api';
import { toast } from 'sonner';
import { formatDate, formatTime } from '../../lib/utils';
import NotificationTemplateModal from './NotificationTemplateModal';
import NotificationViewModal from './NotificationViewModal';

export default function NotificationManagement() {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(false);

  // Dashboard state
  const [dashboardData, setDashboardData] = useState<NotificationDashboard | null>(null);
  const [dashboardPeriod, setDashboardPeriod] = useState<'24h' | '7d' | '30d' | '90d'>('7d');

  // Notifications state
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [notificationsPagination, setNotificationsPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [notificationsFilters, setNotificationsFilters] = useState({
    search: '',
    status: 'all',
    targetType: 'all',
    targetCategory: 'all'
  });
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<NotificationTemplate[]>([]);
  const [templatesPagination, setTemplatesPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    pages: 0
  });
  const [templatesFilters, setTemplatesFilters] = useState({
    search: '',
    category: 'all',
    type: 'all',
    isActive: 'all'
  });
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<NotificationTemplate | null>(null);

  // Send notification state
  const [sendForm, setSendForm] = useState<NotificationCreateRequest>({
    title: '',
    body: '',
    image: '',
    targetType: 'all',
    targetCategory: 'promotions',
    targetUsers: [],
    locationFilter: {},
    data: {}
  });
  const [sendLoading, setSendLoading] = useState(false);
  const [previewData, setPreviewData] = useState<any>(null);

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    notification: Notification | null;
  }>({ isOpen: false, notification: null });

  useEffect(() => {
    if (activeTab === 'dashboard') {
      fetchDashboard();
    } else if (activeTab === 'notifications') {
      fetchNotifications();
    } else if (activeTab === 'templates') {
      fetchTemplates();
    }
  }, [activeTab, dashboardPeriod, notificationsPagination.page, templatesPagination.page]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      const response = await NotificationAPI.getDashboard({ period: dashboardPeriod });
      setDashboardData(response.data);
    } catch (error) {
      toast.error("Failed to fetch dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: notificationsPagination.page,
        limit: notificationsPagination.limit
      };

      if (notificationsFilters.search) params.search = notificationsFilters.search;
      if (notificationsFilters.status !== 'all') params.status = notificationsFilters.status;
      if (notificationsFilters.targetType !== 'all') params.targetType = notificationsFilters.targetType;
      if (notificationsFilters.targetCategory !== 'all') params.targetCategory = notificationsFilters.targetCategory;

      const response = await NotificationAPI.getAll(params);
      setNotifications(response.data.notifications);
      setNotificationsPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      toast.error("Failed to fetch notifications");
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const params: any = {
        page: templatesPagination.page,
        limit: templatesPagination.limit
      };

      if (templatesFilters.search) params.search = templatesFilters.search;
      if (templatesFilters.category !== 'all') params.category = templatesFilters.category;
      if (templatesFilters.type !== 'all') params.type = templatesFilters.type;
      if (templatesFilters.isActive !== 'all') params.isActive = templatesFilters.isActive === 'true';

      const response = await NotificationAPI.templates.getAll(params);
      setTemplates(response.data.templates);
      setTemplatesPagination(prev => ({
        ...prev,
        total: response.data.pagination.total,
        pages: response.data.pagination.pages
      }));
    } catch (error) {
      toast.error("Failed to fetch templates");
    } finally {
      setLoading(false);
    }
  };

  const handleSendNotification = async () => {
    if (!sendForm.title || !sendForm.body) {
      toast.error("Please fill in title and body");
      return;
    }

    setSendLoading(true);
    try {
      const response = await NotificationAPI.send(sendForm);
      toast.success(`Notification sent to ${response.data.deliveryStats.successfulSends} users`);

      // Reset form
      setSendForm({
        title: '',
        body: '',
        image: '',
        targetType: 'all',
        targetCategory: 'promotions',
        targetUsers: [],
        locationFilter: {},
        data: {}
      });

      // Refresh notifications list if on that tab
      if (activeTab === 'notifications') {
        fetchNotifications();
      }
    } catch (error) {
      toast.error("Failed to send notification");
    } finally {
      setSendLoading(false);
    }
  };

  const handlePreviewNotification = async () => {
    try {
      const response = await NotificationAPI.preview({
        targetType: sendForm.targetType,
        targetUsers: sendForm.targetUsers,
        targetCategory: sendForm.targetCategory,
        locationFilter: sendForm.locationFilter
      });
      setPreviewData(response.data);
      toast.success(`Preview Generated: Will target ${response.data.targetCount} users`);
    } catch (error) {
      toast.error("Failed to generate preview");
    }
  };

  const handleDeleteNotification = async () => {
    if (!deleteConfirm.notification) return;

    try {
      await NotificationAPI.delete(deleteConfirm.notification._id);
      toast.success("Notification deleted successfully");
      setDeleteConfirm({ isOpen: false, notification: null });
      fetchNotifications();
    } catch (error) {
      toast.error("Failed to delete notification");
    }
  };

  const getStatusBadge = (status: string) => {
    return <StatusBadge status={status} />;
  };

  const getCategoryBadge = (category: string) => {
    const categoryConfig = {
      'orderUpdates': { color: 'bg-blue-100 text-blue-800', label: 'Order Updates' },
      'promotions': { color: 'bg-green-100 text-green-800', label: 'Promotions' },
      'deliveryUpdates': { color: 'bg-purple-100 text-purple-800', label: 'Delivery Updates' },
      'paymentUpdates': { color: 'bg-orange-100 text-orange-800', label: 'Payment Updates' },
      'appUpdates': { color: 'bg-gray-100 text-gray-800', label: 'App Updates' }
    };

    const config = categoryConfig[category as keyof typeof categoryConfig] || categoryConfig.promotions;

    return (
      <Badge className={config.color}>
        {config.label}
      </Badge>
    );
  };

  const StatsCardSkeleton = () => (
    <Card>
      <CardContent className="p-6">
        <div className="flex items-center space-x-2">
          <Skeleton className="h-8 w-8" />
          <div className="space-y-1">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div className="space-y-6">
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="dashboard">Dashboard</TabsTrigger>
          <TabsTrigger value="send">Send Notification</TabsTrigger>
          <TabsTrigger value="notifications">History</TabsTrigger>
          <TabsTrigger value="templates">Templates</TabsTrigger>
        </TabsList>

        {/* Dashboard Tab */}
        <TabsContent value="dashboard" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Notification Dashboard</h2>
            <Select value={dashboardPeriod} onValueChange={(value: '24h' | '7d' | '30d' | '90d') => setDashboardPeriod(value)}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="24h">24 Hours</SelectItem>
                <SelectItem value="7d">7 Days</SelectItem>
                <SelectItem value="30d">30 Days</SelectItem>
                <SelectItem value="90d">90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {loading ? (
              <>
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
              </>
            ) : dashboardData ? (
              <>
                <StatsCard
                  title="Total Notifications"
                  value={(dashboardData.overview?.totalNotifications || 0).toString()}
                  icon={<Bell className="h-4 w-4 text-blue-600" />}
                />
                <StatsCard
                  title="Total Sent"
                  value={(dashboardData.overview?.deliveryStats?.totalSent || 0).toString()}
                  icon={<Send className="h-4 w-4 text-green-600" />}
                />
                <StatsCard
                  title="Active Users"
                  value={(dashboardData.userStats?.activeUsers || 0).toString()}
                  icon={<Users className="h-4 w-4 text-purple-600" />}
                />
                <StatsCard
                  title="Delivery Rate"
                  value={`${Math.round((dashboardData.overview?.deliveryStats?.avgDeliveryRate || 0) * 100)}%`}
                  icon={<TrendingUp className="h-4 w-4 text-orange-600" />}
                />
              </>
            ) : (
              <>
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
                <StatsCardSkeleton />
              </>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle>Category Breakdown</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : dashboardData?.charts.categoryBreakdown ? (
                  <div className="space-y-2">
                    {dashboardData.charts.categoryBreakdown.map((item, index) => (
                      <div key={index} className="flex justify-between items-center">
                        <span className="text-sm capitalize">{(item._id || 'Uncategorized').toString().replace(/_/g, ' ')}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted-foreground">No data available</p>
                )}
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>User Statistics</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                ) : dashboardData ? (
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Total Users</span>
                      <span className="font-medium">{dashboardData.userStats.totalUsers}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">With FCM Token</span>
                      <span className="font-medium">{dashboardData.userStats.usersWithFCM}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm">Active Users</span>
                      <span className="font-medium">{dashboardData.userStats.activeUsers}</span>
                    </div>
                  </div>
                ) : (
                  <p className="text-muted-foreground">No data available</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Send Notification Tab */}
        <TabsContent value="send" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Send Push Notification</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Title *</label>
                    <Input
                      placeholder="Notification title (max 100 characters)"
                      value={sendForm.title}
                      onChange={(e) => setSendForm(prev => ({ ...prev, title: e.target.value }))}
                      maxLength={100}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Body *</label>
                    <Textarea
                      placeholder="Notification message (max 500 characters)"
                      value={sendForm.body}
                      onChange={(e) => setSendForm(prev => ({ ...prev, body: e.target.value }))}
                      maxLength={500}
                      rows={4}
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium">Image URL (optional)</label>
                    <Input
                      placeholder="https://example.com/image.jpg"
                      value={sendForm.image}
                      onChange={(e) => setSendForm(prev => ({ ...prev, image: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium">Target Type</label>
                    <Select
                      value={sendForm.targetType}
                      onValueChange={(value: 'all' | 'specific' | 'category' | 'location') =>
                        setSendForm(prev => ({ ...prev, targetType: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="category">By Category</SelectItem>
                        <SelectItem value="location">By Location</SelectItem>
                        <SelectItem value="specific">Specific Users</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <label className="text-sm font-medium">Category</label>
                    <Select
                      value={sendForm.targetCategory}
                      onValueChange={(value: 'orderUpdates' | 'promotions' | 'deliveryUpdates' | 'paymentUpdates' | 'appUpdates') =>
                        setSendForm(prev => ({ ...prev, targetCategory: value }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="orderUpdates">Order Updates</SelectItem>
                        <SelectItem value="promotions">Promotions</SelectItem>
                        <SelectItem value="deliveryUpdates">Delivery Updates</SelectItem>
                        <SelectItem value="paymentUpdates">Payment Updates</SelectItem>
                        <SelectItem value="appUpdates">App Updates</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {previewData && (
                    <Card>
                      <CardContent className="p-4">
                        <h4 className="font-medium mb-2">Preview Results</h4>
                        <p className="text-sm text-muted-foreground">
                          Target Count: <span className="font-medium">{previewData.targetCount}</span> users
                        </p>
                        {previewData.locationBreakdown && (
                          <div className="mt-2">
                            <p className="text-xs text-muted-foreground">Location breakdown:</p>
                            {Object.entries(previewData.locationBreakdown).map(([location, count]) => (
                              <p key={location} className="text-xs">
                                {location}: {count as number}
                              </p>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </div>
              </div>

              <div className="flex gap-2 pt-4">
                <Button onClick={handlePreviewNotification} variant="outline">
                  <Eye className="h-4 w-4 mr-2" />
                  Preview
                </Button>
                <Button onClick={handleSendNotification} disabled={sendLoading}>
                  {sendLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Send Notification
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Notifications History Tab */}
        <TabsContent value="notifications" className="space-y-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex gap-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search notifications..."
                  className="pl-8 w-full sm:w-[300px]"
                  value={notificationsFilters.search}
                  onChange={(e) => setNotificationsFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <Select
                value={notificationsFilters.status}
                onValueChange={(value) => setNotificationsFilters(prev => ({ ...prev, status: value }))}
              >
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="sending">Sending</SelectItem>
                  <SelectItem value="sent">Sent</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={fetchNotifications}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Title</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Target</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent/Targeted</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                    </TableRow>
                  ))
                ) : notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No notifications found
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((notification) => (
                    <TableRow key={notification._id}>
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-medium">{notification.title}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {notification.body}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {notification.targetCategory && getCategoryBadge(notification.targetCategory)}
                      </TableCell>
                      <TableCell className="capitalize">{notification.targetType}</TableCell>
                      <TableCell>{getStatusBadge(notification.status)}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{notification.totalSent}/{notification.totalTargeted}</div>
                          {notification.totalFailed > 0 && (
                            <div className="text-red-600">{notification.totalFailed} failed</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatDate(notification.createdAt)}</div>
                          <div className="text-muted-foreground">{formatTime(notification.createdAt)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedNotification(notification);
                              setShowViewModal(true);
                            }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          {notification.status === 'sent' || notification.status === 'failed' ? (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setDeleteConfirm({ isOpen: true, notification })}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          ) : null}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {notificationsPagination.pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={notificationsPagination.page === 1}
                onClick={() => setNotificationsPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>
              <span className="flex items-center px-4">
                Page {notificationsPagination.page} of {notificationsPagination.pages}
              </span>
              <Button
                variant="outline"
                disabled={notificationsPagination.page >= notificationsPagination.pages}
                onClick={() => setNotificationsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Templates Tab */}
        <TabsContent value="templates" className="space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">Notification Templates</h2>
            <Button onClick={() => {
              setEditingTemplate(null);
              setShowTemplateModal(true);
            }}>
              <Plus className="h-4 w-4 mr-2" />
              Create Template
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-between items-center">
            <div className="flex gap-4 w-full sm:w-auto">
              <div className="relative w-full sm:w-auto">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search templates..."
                  className="pl-8 w-full sm:w-[300px]"
                  value={templatesFilters.search}
                  onChange={(e) => setTemplatesFilters(prev => ({ ...prev, search: e.target.value }))}
                />
              </div>
              <Select
                value={templatesFilters.category}
                onValueChange={(value) => setTemplatesFilters(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="orderUpdates">Order Updates</SelectItem>
                  <SelectItem value="promotions">Promotions</SelectItem>
                  <SelectItem value="deliveryUpdates">Delivery Updates</SelectItem>
                  <SelectItem value="paymentUpdates">Payment Updates</SelectItem>
                  <SelectItem value="appUpdates">App Updates</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Button onClick={fetchTemplates}>
              <Search className="h-4 w-4 mr-2" />
              Search
            </Button>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Usage</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 5 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell><Skeleton className="h-4 w-40" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-20" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-12" /></TableCell>
                      <TableCell><Skeleton className="h-6 w-16" /></TableCell>
                      <TableCell><Skeleton className="h-4 w-24" /></TableCell>
                      <TableCell><Skeleton className="h-8 w-20" /></TableCell>
                    </TableRow>
                  ))
                ) : templates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center py-8 text-muted-foreground">
                      No templates found
                    </TableCell>
                  </TableRow>
                ) : (
                  templates.map((template) => (
                    <TableRow key={template._id}>
                      <TableCell className="font-medium">
                        <div>
                          <div className="font-medium">{template.name}</div>
                          <div className="text-sm text-muted-foreground truncate max-w-xs">
                            {template.description}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>{getCategoryBadge(template.category)}</TableCell>
                      <TableCell className="capitalize">{template.type}</TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{template.usage.timesUsed} times</div>
                          <div className="text-muted-foreground">{template.usage.totalSent} sent</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={template.isActive ? 'active' : 'inactive'} />
                      </TableCell>
                      <TableCell>
                        <div className="text-sm">
                          <div>{formatDate(template.createdAt)}</div>
                          <div className="text-muted-foreground">{formatTime(template.createdAt)}</div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setEditingTemplate(template);
                              setShowTemplateModal(true);
                            }}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSendForm({
                                ...sendForm,
                                title: template.title,
                                body: template.body,
                                targetCategory: template.category,
                              });
                              setActiveTab('send');
                              toast.success("Template loaded into send form");
                            }}
                          >
                            <Send className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Card>

          {templatesPagination.pages > 1 && (
            <div className="flex justify-center gap-2">
              <Button
                variant="outline"
                disabled={templatesPagination.page === 1}
                onClick={() => setTemplatesPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </Button>
              <span className="flex items-center px-4">
                Page {templatesPagination.page} of {templatesPagination.pages}
              </span>
              <Button
                variant="outline"
                disabled={templatesPagination.page >= templatesPagination.pages}
                onClick={() => setTemplatesPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </Button>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteConfirm.isOpen} onOpenChange={(open) => setDeleteConfirm({ isOpen: open, notification: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Notification</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this notification? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteNotification}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Modals */}
      {showTemplateModal && (
        <NotificationTemplateModal
          template={editingTemplate}
          onClose={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
          }}
          onSuccess={() => {
            setShowTemplateModal(false);
            setEditingTemplate(null);
            fetchTemplates();
          }}
        />
      )}

      {showViewModal && selectedNotification && (
        <NotificationViewModal
          notification={selectedNotification}
          onClose={() => {
            setShowViewModal(false);
            setSelectedNotification(null);
          }}
        />
      )}
    </div>
  );
}